/**
 * How many campaigns exist, in which state, and how many people have told us
 * to stop.
 *
 * EVERY STATE IS DRAWN, INCLUDING THE ZEROES. "Active: 0" is the single most
 * useful figure on this card - it is the difference between a sender that is
 * broken and a sender with nothing to do - and a card that only listed the
 * non-empty states would answer that question by leaving the row out.
 *
 * The opt-out list is here rather than on its own because it belongs to the
 * same portfolio: it is the count of everyone who may never be written to
 * again, whichever campaign taught them to ask.
 */
import { Link } from "react-router-dom";
import { ChevronRight } from "lucide-react";
import { useT } from "../../../../i18n/I18nProvider";
import type { StatusTotals } from "../../../../lib/marketingOverview";
import { Panel, Stat, num } from "../shared";
import { count } from "./lib";

/** The campaign states, in the order a campaign passes through them. */
const CAMPAIGN_STATES = ["draft", "scheduled", "active", "paused", "done"] as const;

export function CampaignMixCard({
  campaigns,
  suppression,
}: {
  campaigns: StatusTotals;
  suppression: { total: number; bySource: StatusTotals };
}) {
  const t = useT();
  const total = CAMPAIGN_STATES.reduce((n, s) => n + count(campaigns, s), 0);

  return (
    <Panel
      title={t("marketing.overview.mix.title")}
      description={t("marketing.overview.mix.intro", { n: num(total) })}
      actions={
        <Link to="/marketing/suppression" className="crm-btn">
          {t("nav.suppression")}
          <ChevronRight className="size-4" />
        </Link>
      }
    >
      <div className="grid grid-cols-2 gap-3 sm:grid-cols-3">
        {CAMPAIGN_STATES.map((state) => {
          const n = count(campaigns, state);
          return (
            <Stat
              key={state}
              label={t(`marketing.status.${state}`)}
              value={num(n)}
              // Green only when there IS one. A green nought beside "active"
              // reads as a clean bill of health on the very figure that
              // explains why nothing is going out.
              tone={state === "active" ? (n > 0 ? "good" : "muted") : "neutral"}
            />
          );
        })}
        <Stat
          label={t("marketing.overview.mix.suppressed")}
          value={num(suppression.total)}
          sub={
            // Where the entries came from. An opt-out somebody clicked and an
            // address a bounce struck off are the same instruction to the
            // sender and very different news about the list.
            Object.entries(suppression.bySource)
              .map(([source, n]) => `${t(`marketing.suppression.source.${source}`)} ${num(n)}`)
              .join(", ") || undefined
          }
          tone="muted"
        />
      </div>
    </Panel>
  );
}
